import { Tooltip } from "@mui/material";
import {
  useDashboardPageContext,
  useFormatZoned,
  useSelectedDay,
} from "utils/customHooks/genericHooks";
import { IconButton } from "@mui/material";
import { useFlightTranslation } from "utils/customHooks/useFlightTranslation";
import { ChevronRight } from "@mui/icons-material";
import { goToDay } from "providers/GlobalStateProvider";
import { useSettings } from "contexts/SettingsManager/SettingsManager";
import { addDays, subDays } from "date-fns";
import { useDispatch } from "react-redux";
import { getFullDayRangeTitle } from "utils/timeUtils";
import { useTimeZoneMsOffset } from "utils/timeZoneHooks";
import { DatePickerButton } from "./DatePickerButton";
import { DateAndShiftPickerStyles } from "./DateAndShiftPickerStyles";
import { AnimatedShiftButtonsLeftSide } from "./AnimatedShiftButtonsLeftSide";
import ErrorBoundary from "components/SimpleComponents/Errors/ErrorBoundary";
import { useIsDarkMode } from "store/atoms";
import { useTransition } from "react";
import { useBreakpoints } from "constants/constants";

export function DateAndShiftPickerMode() {
  const [isDarkMode] = useIsDarkMode();
  const { isTabletOrLarger } = useBreakpoints();
  return (
    <DateAndShiftPickerStyles
      className="DateAndShiftPickerStyles"
      {...{
        $isDarkMode: isDarkMode,
      }}
    >
      <ErrorBoundary id="AnimatedShiftButtonsLeftSide">
        <AnimatedShiftButtonsLeftSide />
      </ErrorBoundary>
      <div className="rightSide">
        <BackButton />
        {isTabletOrLarger ? null : (
          <ForwardButton className="horizontalLayout" />
        )}
        <ErrorBoundary id="DatePickerButton">
          <DatePickerButton />
        </ErrorBoundary>
        {isTabletOrLarger ? <ForwardButton className="verticalLayout" /> : null}
      </div>
    </DateAndShiftPickerStyles>
  );
}

function BackButton() {
  const dispatch = useDispatch();
  const selectedDay = useSelectedDay();

  const { ge } = (selectedDay && selectedDay.range) || {
    ge: null,
    lt: null,
  };

  const { dayStartMsOffset } = useSettings();
  const { isFullDaySelected } = useDashboardPageContext();
  const formatZoned = useFormatZoned();
  const timeZoneMsOffset = useTimeZoneMsOffset();
  const [isPending, startTransition] = useTransition();
  const onPrevious = () => {
    const nextGe = subDays(ge, 1).valueOf();
    const nextLt = ge;
    startTransition(() => {
      dispatch(
        goToDay({
          newSelectedDay: {
            shift: { name: getFullDayRangeTitle(nextGe, nextLt, formatZoned) },
            range: {
              ge: nextGe,
              lt: nextLt,
            },
          },
          dayStartMsOffset,
          timeZoneMsOffset,
          shouldGoToFirstShiftInDay: !isFullDaySelected,
          formatZoned,
        })
      );
    });
  };
  const { translate } = useFlightTranslation();
  return (
    <Tooltip title={translate("Previous_Day")} disableInteractive={true}>
      <IconButton
        className="btnBack"
        onClick={onPrevious}
        disabled={isPending}
        style={{ cursor: isPending ? "wait" : "pointer" }}
      >
        <ChevronRight style={{ transform: "rotate(180deg)" }} />
      </IconButton>
    </Tooltip>
  );
}
function ForwardButton({ className }: { className?: string }) {
  const selectedDay = useSelectedDay();

  const { lt } = (selectedDay && selectedDay.range) || {
    ge: null,
    lt: null,
  };
  const now = Date.now();

  const dispatch = useDispatch();
  const showBtnForwardCondition = lt <= now;

  const { dayStartMsOffset } = useSettings();
  const { isFullDaySelected } = useDashboardPageContext();
  const formatZoned = useFormatZoned();
  const timeZoneMsOffset = useTimeZoneMsOffset();
  const [isPending, startTransition] = useTransition();

  const onNext = () => {
    const nextGe = lt;
    const nextLt = addDays(lt, 1).valueOf();
    startTransition(() => {
      dispatch(
        goToDay({
          newSelectedDay: {
            shift: { name: getFullDayRangeTitle(nextGe, nextLt, formatZoned) },
            range: {
              ge: nextGe,
              lt: nextLt,
            },
          },
          dayStartMsOffset,
          timeZoneMsOffset,
          shouldGoToFirstShiftInDay: !isFullDaySelected,
          formatZoned,
        })
      );
    });
  };
  const { translate } = useFlightTranslation();
  const disabled = !showBtnForwardCondition || isPending;
  return (
    <Tooltip
      title={disabled ? "" : translate("Next_Day")}
      disableInteractive={true}
    >
      <IconButton
        disabled={disabled}
        className={`btnForward ${className}`}
        onClick={onNext}
        style={{ cursor: isPending ? "wait" : "pointer" }}
      >
        <ChevronRight />
      </IconButton>
    </Tooltip>
  );
}
